import {
  CardContainer,
  CollapseSection,
  LabeledValue,
  useFormatting,
} from "@msys/ui";
import { Divider, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useFormikContext } from "formik";
import { isNil } from "lodash-es";
import React from "react";
import { BillOfMaterialsItemFragment } from "../../bill-of-materials/BillOfMaterials.generated.js";

interface Props {
  materialItem: BillOfMaterialsItemFragment;
  QuantityInput: React.ReactElement;
  PriceInput: React.ReactElement;
}

export const OrderDetailsBox = ({
  materialItem,
  QuantityInput,
  PriceInput,
}: Props) => {
  const { t } = useTranslate(["Materials", "PurchaseOrders"]);
  const { getFormattedPrice } = useFormatting();

  return (
    <CardContainer
      title={t("Order details", {
        ns: "Materials",
      })}
      isExpandable
    >
      <Stack
        direction="column"
        spacing={0}
        sx={{
          margin: 1,
        }}
      >
        <CollapseSection
          title={t("Material quantity", {
            ns: "Materials",
          })}
          titleVariant="h4"
          isInitiallyExpanded
        >
          <Stack
            spacing={1}
            direction={"column"}
            sx={{
              padding: 1,
            }}
          >
            <Stack
              spacing={1}
              direction="row"
              sx={{
                justifyContent: "space-between",
              }}
            >
              <Typography>
                {t("Total required", {
                  ns: "Materials",
                })}
              </Typography>
              <Typography>{`${materialItem.quantityRequired} ${materialItem.quantityUnit}`}</Typography>
            </Stack>
            <Stack
              spacing={1}
              direction="column"
              sx={{
                paddingLeft: 1,
              }}
            >
              <Stack
                spacing={1}
                direction="row"
                sx={{
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2">
                  {t("Accepted quote items", {
                    ns: "Materials",
                  })}
                </Typography>
                <Typography variant="body2">
                  {`${materialItem.quantityRequiredAccepted} ${materialItem.quantityUnit}`}
                </Typography>
              </Stack>
              <Stack
                spacing={1}
                direction="row"
                sx={{
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2">
                  {t("Draft quote items", {
                    ns: "Materials",
                  })}
                </Typography>
                <Typography variant="body2">
                  {`${materialItem.quantityRequiredDraft} ${materialItem.quantityUnit}`}
                </Typography>
              </Stack>
              <Stack
                spacing={1}
                direction="row"
                sx={{
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2">
                  {t("Pending quote items", {
                    ns: "Materials",
                  })}
                </Typography>
                <Typography variant="body2">
                  {`${materialItem.quantityRequiredPending} ${materialItem.quantityUnit}`}
                </Typography>
              </Stack>
              <Stack
                spacing={1}
                direction="row"
                sx={{
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2">
                  {t("Items with pending change order", {
                    ns: "Materials",
                  })}
                </Typography>
                <Typography variant="body2">
                  {`${materialItem.quantityRequiredChange} ${materialItem.quantityUnit}`}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              spacing={1}
              direction="row"
              sx={{
                justifyContent: "space-between",
              }}
            >
              <Typography>
                {t("Ordered", {
                  ns: "Materials",
                })}
              </Typography>
              <Typography>{`${materialItem.quantityOrdered} ${materialItem.quantityUnit}`}</Typography>
            </Stack>
            <Stack
              spacing={1}
              direction="row"
              sx={{
                justifyContent: "space-between",
              }}
            >
              <Typography>
                {t("To be ordered", {
                  ns: "Materials",
                })}
              </Typography>
              <Typography>{`${Math.max(
                materialItem.quantityRequired - materialItem.quantityOrdered,
                0
              )} ${materialItem.quantityUnit}`}</Typography>
            </Stack>
            <Stack
              spacing={1}
              direction="row"
              sx={{
                justifyContent: "space-between",
              }}
            >
              <Typography>
                {t("Order quantity", {
                  ns: "PurchaseOrders",
                })}
              </Typography>
              {QuantityInput}
            </Stack>
          </Stack>
        </CollapseSection>
        <CollapseSection
          title={t("Material pricing", {
            ns: "Materials",
          })}
          titleVariant="h4"
          isInitiallyExpanded
        >
          <Stack
            spacing={1}
            direction={"column"}
            sx={{
              padding: 1,
            }}
          >
            <Stack
              spacing={1}
              direction="row"
              sx={{
                justifyContent: "space-between",
              }}
            >
              <Typography>
                {t("List price", {
                  ns: "Materials",
                })}
              </Typography>
              <Typography>
                {!isNil(materialItem.listPrice)
                  ? getFormattedPrice(materialItem.listPrice)
                  : "-"}
              </Typography>
            </Stack>
            <Stack
              spacing={1}
              direction="row"
              sx={{
                justifyContent: "space-between",
              }}
            >
              <Typography>
                {t("Net price", {
                  ns: "Materials",
                })}
              </Typography>
              <Typography>
                {!isNil(materialItem.netPrice)
                  ? getFormattedPrice(materialItem.netPrice)
                  : "-"}
              </Typography>
            </Stack>

            <Stack
              spacing={1}
              direction="row"
              sx={{
                justifyContent: "space-between",
              }}
            >
              <Typography>
                {t("Order price", {
                  ns: "PurchaseOrders",
                })}
              </Typography>
              {PriceInput}
            </Stack>
          </Stack>
        </CollapseSection>
      </Stack>
      <Divider />
      <Stack
        spacing={1}
        direction="column"
        sx={{
          padding: 1,
        }}
      >
        <Typography variant="h4">
          {t("Order summary", {
            ns: "PurchaseOrders",
          })}
        </Typography>
        <Stack
          spacing={1}
          direction="row"
          sx={{
            justifyContent: "space-between",
          }}
        >
          <LabeledValue
            label={t("Order quantity", {
              ns: "PurchaseOrders",
            })}
          >
            <MaterialItemQuantity item={materialItem} />
          </LabeledValue>
          <Typography color="secondary">x</Typography>
          <LabeledValue
            label={t("Order price", {
              ns: "PurchaseOrders",
            })}
          >
            <MaterialItemUnitPrice item={materialItem} />
          </LabeledValue>
          <Typography color="secondary">=</Typography>
          <LabeledValue
            label={t("Total price", {
              ns: "PurchaseOrders",
            })}
            align="right"
            style={{ fontSize: "18px", fontWeight: 500 }}
          >
            <MaterialItemTotalPrice item={materialItem} />
          </LabeledValue>
        </Stack>
      </Stack>
    </CardContainer>
  );
};

interface FormValues {
  [key: string]: {
    quantityOrdered: number;
    unitPrice: number;
  };
}

export const MaterialItemTotalPrice = ({
  item,
}: {
  item: BillOfMaterialsItemFragment;
}) => {
  const { getFormattedPrice } = useFormatting();
  const { values } = useFormikContext<FormValues>();
  const value = values[item.id];
  return getFormattedPrice(
    (value?.quantityOrdered ?? 0) * (value?.unitPrice ?? 0)
  );
};

export const MaterialItemQuantity = ({
  item,
}: {
  item: BillOfMaterialsItemFragment;
}) => {
  const { values } = useFormikContext<FormValues>();
  const value = values[item.id];
  return value?.quantityOrdered ?? 0;
};

export const MaterialItemUnitPrice = ({
  item,
}: {
  item: BillOfMaterialsItemFragment;
}) => {
  const { getFormattedPrice } = useFormatting();
  const { values } = useFormikContext<FormValues>();
  const value = values[item.id];
  return getFormattedPrice(value?.unitPrice ?? 0);
};
